import type {
  InfiniteData,
  InfiniteQueryObserverOptions,
  QueryClient,
  QueryKey,
} from "@tanstack/react-query";
import type { Getter } from "jotai";
import { atomWithInfiniteQuery } from "jotai-tanstack-query";

import {
  shouldRetryBackendRead,
  unwrapBackendResult,
  type BackendFailure,
  type BackendFailureError,
} from "#/shared/lib/backend-api";
import type { ResultAsync } from "#/shared/lib/result";

import type {
  ResourceDefinition,
  ResourceQueryKey,
  ResourceQueryReference,
} from "./definition";

/** 游标分页响应的最小形状；`nextCursor` 为 null 表示已到末页。 */
export interface CursorPage {
  nextCursor: string | null;
}

type ResourceInfiniteQueryOptions<TPage extends CursorPage> = Pick<
  InfiniteQueryObserverOptions<
    TPage,
    BackendFailureError,
    InfiniteData<TPage>,
    QueryKey,
    string | null
  >,
  "refetchInterval" | "refetchOnMount" | "refetchOnWindowFocus"
> & {
  staleTime?: number;
};

interface ResourceInfiniteQueryConfig<
  TQueryName extends string,
  TInput,
  TPage extends CursorPage,
> extends ResourceInfiniteQueryOptions<TPage> {
  key: (input: TInput) => readonly unknown[];
  name: TQueryName;
  queryFn: (
    input: TInput,
    cursor: string | null,
  ) => ResultAsync<TPage, BackendFailure>;
  resource: ResourceDefinition<string>;
}

export interface ResourceInfiniteQueryDefinition<
  TInput,
  TPage extends CursorPage,
> extends ResourceQueryReference {
  getKey: (input: TInput) => ResourceQueryKey;
  getOptions: () => ResourceInfiniteQueryOptions<TPage>;
  queryFn: (input: TInput, cursor: string | null) => Promise<TPage>;
}

/**
 * 游标列表的唯一定义入口，与 `defineResourceQuery` 并列：
 * 后者只造单页查询，翻页要的 pageParam 和页拼接不在它的契约里。
 * key、重试和缓存策略都收在这里，调用方只拿到 atom。
 */
export function defineResourceInfiniteQuery<
  const TQueryName extends string,
  TInput,
  TPage extends CursorPage,
>(
  definition: ResourceInfiniteQueryConfig<TQueryName, TInput, TPage>,
): ResourceInfiniteQueryDefinition<TInput, TPage> {
  const { key, name, queryFn, resource, ...options } = definition;
  const scopeKey = [resource.name, name] as const;

  assertValidResourceStaleTime(options.staleTime);

  return {
    getKey: (input) => [...scopeKey, ...key(input)] as const,
    getOptions: () => options,
    queryFn: (input, cursor) => unwrapBackendResult(queryFn(input, cursor)),
    scopeKey,
  };
}

function assertValidResourceStaleTime(staleTime: number | undefined): void {
  if (
    staleTime !== undefined &&
    (!Number.isFinite(staleTime) || staleTime < 0)
  ) {
    throw new Error("Resource query staleTime 必须是非负有限值");
  }
}

export function createResourceInfiniteQueryAtom<
  TInput,
  TPage extends CursorPage,
>(
  query: ResourceInfiniteQueryDefinition<TInput, TPage>,
  readInput: (get: Getter) => TInput,
  getQueryClient?: (get: Getter) => QueryClient,
) {
  return atomWithInfiniteQuery<
    TPage,
    BackendFailureError,
    InfiniteData<TPage>,
    QueryKey,
    string | null
  >((get) => {
    const input = readInput(get);

    return {
      ...query.getOptions(),
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      initialPageParam: null,
      queryFn: ({ pageParam }) => query.queryFn(input, pageParam),
      queryKey: query.getKey(input),
      retry: shouldRetryBackendRead,
    };
  }, getQueryClient);
}
